import { useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { AvatarBoError } from '../../shared/AvatarBoClient'
import {
  fieldErrorsFromIssues,
  type SectionFieldErrors,
} from '../../shared/section-form'
import type { AvatarBoClient, UpsertSectionInput } from '../../shared/types'
import { avatarBoKeys } from './useAvatarCatalogue'

export interface UseSectionMutationOpts {
  client: AvatarBoClient
  setId?: string
  onSaved?: (input: UpsertSectionInput) => void
}

export function useSectionMutation({
  client,
  setId,
  onSaved,
}: UseSectionMutationOpts) {
  const qc = useQueryClient()
  const [serverErrors, setServerErrors] = useState<SectionFieldErrors>({})

  const save = useMutation(
    (input: UpsertSectionInput) => client.upsertSection(input),
    {
      onMutate: () => setServerErrors({}),
      onSuccess: (_result, input) => {
        // Never optimistic: this is a whole-row upsert, so the server's echo is the only
        // trustworthy picture of what the section now is.
        if (setId) {
          qc.invalidateQueries(avatarBoKeys.catalogue(setId))
        }
        qc.invalidateQueries(avatarBoKeys.sets())
        onSaved?.(input)
      },
      onError: (error: unknown) => {
        setServerErrors(
          error instanceof AvatarBoError && error.issues.length
            ? fieldErrorsFromIssues(error.issues)
            : { form: messageFor(error) },
        )
      },
    },
  )

  return { save, serverErrors, clearServerErrors: () => setServerErrors({}) }
}

function messageFor(error: unknown): string {
  if (error instanceof AvatarBoError) {
    return error.code
  }

  return error instanceof Error ? error.message : String(error)
}
